import { apiClient, ApiResponse } from '../api/apiClient';

// Session ID Management
// ✅ Uses sessionStorage + 5-min timeout + beforeunload detection
// - New session if: no session OR last activity > 5 minutes ago
const SESSION_KEY = 'vatli365_session_id';
const LAST_ACTIVITY_KEY = 'vatli365_last_activity';
const SESSION_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes (more accurate than 30)
const TRACKING_DOMAIN = 'ebook';
const TRACKING_URL = '/api/stats/track';

const getOrCreateSessionId = (): string => {
    const now = Date.now();
    const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
    const existingSessionId = sessionStorage.getItem(SESSION_KEY);

    // Check if session expired (>5 min inactive)
    const isExpired = lastActivity && (now - parseInt(lastActivity)) > SESSION_TIMEOUT_MS;

    if (!existingSessionId || isExpired) {
        // Generate new UUID v4
        const sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });

        sessionStorage.setItem(SESSION_KEY, sessionId);
        sessionStorage.setItem(LAST_ACTIVITY_KEY, now.toString());

        if (isExpired) {
            console.log('🔄 Session expired (>5min), new session:', sessionId);
        } else {
            console.log('🆕 New session created:', sessionId);
        }

        return sessionId;
    }

    // Update last activity timestamp
    sessionStorage.setItem(LAST_ACTIVITY_KEY, now.toString());

    return existingSessionId;
};

// ✅ Detect browser/tab close (best effort)
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        const sessionId = sessionStorage.getItem(SESSION_KEY);
        if (sessionId) {
            // Use sendBeacon for reliability (non-blocking, works even when page unloads)
            const data = JSON.stringify({
                session_id: sessionId,
                domain: TRACKING_DOMAIN,
                path: window.location.pathname,
                event_type: 'session_end'
            });

            // sendBeacon is more reliable than fetch for beforeunload
            navigator.sendBeacon(TRACKING_URL, new Blob([data], { type: 'application/json' }));
        }
    });
}

// Endpoint base is handled by apiClient, we just need the suffix relative to API_BASE_URL
// If apiClient base is '/api', and we want '/api/stats/track', we use '/stats/track'

export const trackPageAccess = async (path: string) => {
    try {
        const sessionId = getOrCreateSessionId();
        await apiClient.post('/stats/track', { path, session_id: sessionId, domain: TRACKING_DOMAIN }, true);
    } catch (error) {
        // Silently fail to not disrupt user experience
        console.error("Failed to track page access", error);
    }
};

export interface StatsResponse {
    period: string;
    date_start: string;
    date_end: string;
    total_views: number;
    unique_visitors: number;
    user_views: number;
    active_users: number;
    details: { path: string; count: number }[];
}

export const getAccessStats = async (period: 'day' | 'month' | 'year', date?: string): Promise<StatsResponse> => {
    const params = new URLSearchParams();
    params.append('period', period);
    if (date) params.append('date_str', date);

    const response = await apiClient.get<StatsResponse>(`/stats/view?${params.toString()}`);

    if (response.error) {
        throw new Error(response.error);
    }

    if (!response.data) {
        throw new Error("No data received");
    }

    return response.data;
};

export interface AccessDomainSummary {
    domain: 'ebook' | 'lab' | 'eng' | 'tronde';
    label: string;
    sessions: number;
    page_views: number;
    active_accounts: number;
    new_accounts: number;
}

export interface AccessDashboardResponse {
    period: string;
    date_start: string;
    date_end: string;
    domains: AccessDomainSummary[];
    totals: {
        sessions: number;
        page_views: number;
        active_accounts: number;
        new_accounts: number;
    };
    series: {
        labels: string[];
        sessions: Record<string, number[]>;
        new_accounts: Record<string, number[]>;
        ecosystem_sessions: number[];
        ecosystem_new_accounts: number[];
    };
    top_pages: Record<string, { path: string; count: number }[]>;
}

export const getAccessDashboard = async (
    period: 'day' | 'month' | 'year',
    date?: string
): Promise<AccessDashboardResponse> => {
    const params = new URLSearchParams();
    params.append('period', period);
    if (date) params.append('date_str', date);

    const response = await apiClient.get<AccessDashboardResponse>(`/stats/access-dashboard?${params.toString()}`);

    if (response.error) {
        throw new Error(response.error);
    }

    if (!response.data) {
        throw new Error("No data received");
    }

    return response.data;
};
